import TableError from 'components/TableError';
import useRequest from 'hooks/useRequest';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Col, Row, Table } from 'reactstrap';
import { api } from 'utils/axios';
import { API_BASE_URL, GET_GENDER, MODULE_TYPE, SORT_VALUES_DATE } from 'utils/constants';
import Loader from '../../../components/Loader';
import { useRouter } from '../../../hooks/useRouter';
import Paginate from 'components/Paginate';

const SchoolClinicStudents = ({ schoolClinicsDetails }) => {
  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const fileInputRef = useRef();

  const [loading, setLoading] = useState(false);
  const [studentsList, setStudentsList] = useState([]);
  const [studentsPagination, setStudentsPagination] = useState({});

  const [getStudentsRequest, { loading: getStudentsLoading }] = useRequest({
    url: '/school-clinic/student/search',
    method: 'POST',
    onSuccess: (data) => {
      setStudentsList(data?.results);
      setStudentsPagination(data?.pagination);
    },
  });

  const handleChangePage = async (page) => {
    await getStudentsRequest({
      data: {
        page,
        size: 20,
        ...SORT_VALUES_DATE,
        schoolClinicId: schoolClinicsDetails?.id,
      },
    });
  };

  useEffect(() => {
    if (!isEmpty(schoolClinicsDetails?.id)) {
      handleChangePage(parseInt(router?.query?.page, 10) || 1);
    }
  }, [schoolClinicsDetails?.id]);

  const handleImportStudents = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload only CSV or Excel files.');
      return;
    }

    setLoading(true);

    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      // Attachement upload
      const fileResponse = await api({
        method: 'POST',
        url: `${API_BASE_URL}/attachment/upload`,
        data: formData,
      });

      // Import students using uploaded file reference
      const data = await api({
        method: 'POST',
        url: `${API_BASE_URL}/school-clinic/admin/${schoolClinicsDetails?.id}/import-students`,
        data: fileResponse,
      });

      if (isEmpty(data?.errors) && data?.success !== false) {
        await handleChangePage(1);
        toast.success(data?.message);
      } else {
        toast.error(data?.errors[0] || data?.message || 'Something went wrong!');
      }
    } catch (error) {
      console.error('Error importing students:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4">
      <Loader isActive={getStudentsLoading || loading} />

      <Row className="mb-2 align-items-center">
        <Col sm="4">
          <div className="search-box me-2 d-inline-block">
            <div className="position-relative font-bold">{studentsPagination?.totalCount} Students Found</div>
          </div>
        </Col>
        <Col sm="8">
          <div className="text-sm-end">
            <a
              href="/school-clinic/sample-students.csv"
              className="btn btn-sm btn-link btn-rounded waves-effect waves-light mb-2 me-3">
              <i className="mdi mdi-download me-1" />
              Download Sample file
            </a>
            <button
              onClick={() => {
                fileInputRef.current.click();
              }}
              type="button"
              className="btn btn-sm btn-success btn-rounded waves-effect waves-light mb-2 me-2">
              <i className="mdi mdi-plus me-1" />
              Import Students
            </button>

            <input
              type="file"
              accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              ref={fileInputRef}
              className="d-none"
              onChange={handleImportStudents}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="12">
          <div className="table-responsive" style={{ minHeight: 300 }}>
            <Table className="project-list-table   table-centered table-borderless">
              <thead>
                <tr className="table-warning">
                  <th scope="col">S.No.</th>
                  <th scope="col">Admission number</th>
                  <th scope="col">Name</th>
                  <th scope="col">Gender</th>
                  <th scope="col">Parent Name/Email</th>
                  <th scope="col">Active</th>
                  <th scope="col">Deleted</th>
                </tr>
              </thead>
              <tbody>
                {!isEmpty(studentsList) ? (
                  studentsList?.map((elem, index) => {
                    return (
                      <tr key={`student_${elem?.id}`} className={!elem?.active ? 'bg-danger' : ''}>
                        <td>{index + 1}</td>
                        <td>{elem?.admissionNumber}</td>
                        <td>
                          {elem?.firstName} {elem?.lastName}
                        </td>
                        <td>{GET_GENDER[elem?.gender]}</td>
                        <td>
                          {elem?.parentName}
                          <br />
                          {elem?.parentEmail}
                        </td>
                        <td>
                          <Badge className={elem?.active ? 'bg-success' : 'bg-danger'}>
                            {elem?.active ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          <Badge className={elem?.deleted ? 'bg-success' : 'bg-danger'}>
                            {elem?.deleted ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <TableError />
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
      {!isEmpty(studentsList) && <Paginate pageInfo={studentsPagination} onChangePage={handleChangePage} />}
    </div>
  );
};

export default SchoolClinicStudents;
